import React, { useState } from "react";
import { Card, Checkbox, FormControlLabel, FormGroup, Radio, RadioGroup, Table, TableRow, TableHead, TableCell, TableBody } from "@mui/material";
import { BatchProgress } from "../../../model/batch-progress";
import { OperationCounterType } from "../../../constants/constants";
import { countersService } from "../../../services/counters";
import { orderBy } from "lodash";
import { stringValue } from "../../../../common/field/renderer";
import { t } from "i18next";
import { context } from "../../../../context/context";
import { Permission } from "../../../../context/permission";
import { OperationCounter } from "../../../model/operation-counter";
import { useEffectAsync } from "../../../../common/hooks/use-effect-async";
import { useParams } from "react-router-dom";
import { OperationDataValidationPopup } from "../operation-data-validation/operation-data-validation-popup/operation-data-validation-popup";
import { OperationDataReportPopup } from "../operation-data-validation/operation-data-validation-report-popup/operation-data-report-popup";
import { FeatureToggle, IsFeatureToggleEnabled } from "../../../../helpers/is-feature-toggle-enabled";

import "./style.scss";

export type ReportingFilterMode = "delivery-order" | "product" | "parcel";

interface IReportingFilter {
    mode: ReportingFilterMode;
    isDisplayEndedBatch: boolean;
}

interface IReportingPageProps {
    withOperationDataValidationPopup?: boolean;
    withOperationDataReportPopup?: boolean;
}

export const ReportingPage = (props: IReportingPageProps) => {
    const [batchProgressList, setBatchProgressList] = useState<BatchProgress[]>([]);
    const [generalCounterMap, setGeneralCounterMap] = useState<Map<OperationCounterType, number>>(new Map<OperationCounterType, number>());
    const [filter, setFilter] = useState<IReportingFilter>({ mode: "delivery-order", isDisplayEndedBatch: false });
    const [isOperationDataValidationPopupOpen, setIsOperationDataValidationPopupOpen] = useState(props.withOperationDataValidationPopup ?? false);
    const [isOperationDataReportPopupOpen, setIsOperationDataReportPopupOpen] = useState(props.withOperationDataReportPopup ?? false);

    const params = useParams();

    useEffectAsync(async () => {
        onListLoaded(
            await countersService.loadOperationCounter(
                params.operationCode as string,
                Number(params.batchId)));
    }, [params.operationCode, params.batchId]);

    const onListLoaded = (counterList: OperationCounter[]) => {
        const batchProgressListTemp: BatchProgress[] = [];
        const generalCounterMapTemp: Map<OperationCounterType, number> = new Map<OperationCounterType, number>();

        let batchProgress: BatchProgress | undefined;
        orderBy(counterList, ["batchId"]).forEach(counter => {
            if (!batchProgress) {
                batchProgress = new BatchProgress(counter);
            } else if (batchProgress.batchId !== counter.batchId) {
                batchProgressListTemp.push(batchProgress);
                batchProgress = new BatchProgress(counter);
            }
            batchProgress.counterMap.set(counter.counterName, counter.counterValue);

            if (!generalCounterMapTemp.has(counter.counterName)) {
                generalCounterMapTemp.set(counter.counterName, 0);
            }
            generalCounterMapTemp.set(counter.counterName,
                generalCounterMapTemp.get(counter.counterName)! + counter.counterValue);
        });
        if (batchProgress !== undefined) {
            batchProgressListTemp.push(batchProgress);
        }

        setBatchProgressList(batchProgressListTemp);
        setGeneralCounterMap(generalCounterMapTemp);
    };

    const renderProgressModeData = (
        counterMap: ReadonlyMap<OperationCounterType, number>,
        mode: ReportingFilterMode) => {
        switch (mode) {
            case "product":
                return renderBatchProductProgress(counterMap);
            case "delivery-order":
                return renderBatchDeliveryOrderProgress(counterMap);
            case "parcel":
                return renderBatchParcelProgress(counterMap);
        }
    };

    const renderProgress = (
        counterMap: ReadonlyMap<OperationCounterType, number>,
        isFinished: boolean,
        batchProgress?: BatchProgress) => {

        const title = batchProgress ? `${stringValue(batchProgress, "operationCode")} -
            ${stringValue(batchProgress, "batchId")}
            (${stringValue(batchProgress, "batchCreationDate")})` : t("reporting.totalLine");
        const status = batchProgress ? stringValue(batchProgress, "batchStatusLabel") : "";
        const warehouseId = batchProgress ? batchProgress.warehouseId : "";
        const logisticCommitmentDate = batchProgress ? stringValue(batchProgress, "logisticCommitmentDate") : "";

        const className = isFinished ? "finished" : (batchProgress ? batchProgress.logisticCommitmentDateLevel : "");

        return <TableRow key={title} className={className}>
            <TableCell className="counterTitle">
                <div>{title}</div>
                <div>{status}</div>
            </TableCell>
            <TableCell>{warehouseId}</TableCell>
            <TableCell>{logisticCommitmentDate}</TableCell>

            {renderProgressModeData(counterMap, filter.mode)}

        </TableRow>;
    };

    const renderProgressModeHeaders = (
        mode: ReportingFilterMode) => {
        switch (mode) {
            case "product":
                return <TableRow>
                    <TableCell/>
                    <TableCell/>
                    <TableCell>{t("model.batch.logisticCommitmentDate")}</TableCell>
                    <TableCell colSpan={2} className="counterCol">
                        {t(`reporting.headerProduct.nbAvailable`)}</TableCell>
                    <TableCell colSpan={2} className="counterCol">
                        {t(`reporting.headerProduct.nbToLabel`)}</TableCell>
                    {context.hasPermission(Permission.FeatureReadyToShip) && <TableCell colSpan={2} className="counterCol">
                        {t(`reporting.headerProduct.nbToPrepareToShip`)}</TableCell>}
                    <TableCell colSpan={2} className="counterCol">
                        {t(`reporting.headerProduct.nbToShip`)}</TableCell>
                    <TableCell colSpan={2} className="counterCol">
                        {t(`reporting.headerProduct.nbShipped`)}</TableCell>
                    {IsFeatureToggleEnabled(FeatureToggle.STOCKOUTS) && <TableCell colSpan={2} className="counterCol">
                        {t(`reporting.headerProduct.nbStockout`)}</TableCell>}
                    <TableCell>{t(`reporting.headerProduct.nbTotal`)}</TableCell>
                </TableRow>;

            case "delivery-order":
                return <TableRow>
                    <TableCell/>
                    <TableCell/>
                    <TableCell>{t("model.batch.logisticCommitmentDate")}</TableCell>
                    <TableCell colSpan={2} className="counterCol">
                        {t(`reporting.headerDeliveryOrder.nbAvailable`)}</TableCell>
                    <TableCell colSpan={2} className="counterCol">
                        {t(`reporting.headerDeliveryOrder.nbToLabel`)}</TableCell>
                    {context.hasPermission(Permission.FeatureReadyToShip) && <TableCell colSpan={2} className="counterCol">
                        {t(`reporting.headerDeliveryOrder.nbToPrepareToShip`)}</TableCell>}
                    <TableCell colSpan={2} className="counterCol">
                        {t(`reporting.headerDeliveryOrder.nbToShip`)}</TableCell>
                    <TableCell colSpan={2} className="counterCol">
                        {t(`reporting.headerDeliveryOrder.nbShipped`)}</TableCell>
                    <TableCell colSpan={2} className="counterCol">
                        {t(`reporting.headerDeliveryOrder.nbCanceled`)}</TableCell>
                    {IsFeatureToggleEnabled(FeatureToggle.STOCKOUTS) && <TableCell colSpan={2} className="counterCol">
                        {t(`reporting.headerDeliveryOrder.nbStockout`)}</TableCell>}
                    <TableCell>{t(`reporting.headerDeliveryOrder.nbTotal`)}</TableCell>
                </TableRow>;

            case "parcel":
                return <TableRow>
                    <TableCell/>
                    <TableCell/>
                    <TableCell>{t("model.batch.logisticCommitmentDate")}</TableCell>
                    <TableCell colSpan={2} className="counterCol">
                        {t(`reporting.headerParcel.nbToLabel`)}</TableCell>
                    {context.hasPermission(Permission.FeatureReadyToShip) && <TableCell colSpan={2} className="counterCol">
                        {t(`reporting.headerParcel.nbToPrepareToShip`)}</TableCell>}
                    <TableCell colSpan={2} className="counterCol">
                        {t(`reporting.headerParcel.nbToShip`)}</TableCell>
                    <TableCell colSpan={2} className="counterCol">
                        {t(`reporting.headerParcel.nbShipped`)}</TableCell>
                    <TableCell>{t(`reporting.headerParcel.nbTotal`)}</TableCell>
                </TableRow>;
        }
    };

    const renderBatchProgress = (batchProgress: BatchProgress) => {
        if (isBatchProgressDisplayed(batchProgress)) {
            return renderProgress(
                batchProgress.counterMap,
                batchProgress.batchStatus === "Done",
                batchProgress);
        }
        return undefined;
    };

    const isBatchProgressDisplayed = (batchProgress: BatchProgress) => {
        return filter.isDisplayEndedBatch || batchProgress.batchStatus !== "Done";
    };

    const renderFilter = () => {
        return <Card className="reporting-filter" elevation={0}>
            <RadioGroup
                value={`${filter.mode}`}
                onChange={(event: any) => setFilter({...filter, mode: event.target.value})}
                style={{ flexDirection: "row" }}
            >
                <FormControlLabel
                    value={"delivery-order"} control={<Radio />}
                    label={t("reporting.filter.showDeliveryOrder")} />
                <FormControlLabel
                    value={"parcel"} control={<Radio />}
                    label={t("reporting.filter.showParcel")} />
                <FormControlLabel
                    value={"product"} control={<Radio />}
                    label={t("reporting.filter.showProduct")} />
            </RadioGroup>
            <FormGroup row>
                <FormControlLabel
                    control={
                        <Checkbox
                            color="primary"
                            checked={filter.isDisplayEndedBatch}
                            onChange={() => setFilter({...filter, isDisplayEndedBatch: !filter.isDisplayEndedBatch})}
                        />
                    }
                    label={t("reporting.filter.showEndedBatch")}
                />
            </FormGroup>
        </Card>;
    };

    const renderBatchDeliveryOrderProgress = (counterMap: ReadonlyMap<OperationCounterType, number>) => {
        return renderCounterColumns({
            nbAvailable: counterMap.get("NbDoAvailable")!,
            nbParcelled: counterMap.get("NbDoParceled")!,
            nbLabeled: counterMap.get("NbDoLabeled")!,
            nbReadyToShip: counterMap.get("NbDoReadyToShip")!,
            nbShipped: counterMap.get("NbDoShipped")!,
            nbCanceled: counterMap.get("NbDoCanceled")!,
            nbStockout: counterMap.get("NbDoStockout")!,
            nbTotal: counterMap.get("NbDo")! - counterMap.get("NbDoCanceled")!,
        });
    };

    const renderBatchParcelProgress = (counterMap: ReadonlyMap<OperationCounterType, number>) => {
        return renderCounterColumns({
            nbAvailable: undefined,
            nbParcelled: counterMap.get("NbParcelParcelled")!,
            nbLabeled: counterMap.get("NbParcelLabeled")!,
            nbReadyToShip: counterMap.get("NbParcelReadyToShip")!,
            nbShipped: counterMap.get("NbParcelShipped")!,
            nbCanceled: undefined,
            nbStockout: undefined,
            nbTotal: counterMap.get("NbParcel")!,
        });
    };

    const renderBatchProductProgress = (counterMap: ReadonlyMap<OperationCounterType, number>) => {
        return renderCounterColumns({
            nbAvailable: counterMap.get("NbProduct")!
                - counterMap.get("NbProductParcelled")!
                - counterMap.get("NbProductLabeled")!
                - counterMap.get("NbProductShipped")!
                - counterMap.get("NbProductReadyToShip")!
                - counterMap.get("NbProductStockout")!,
            nbParcelled: counterMap.get("NbProductParcelled")!,
            nbLabeled: counterMap.get("NbProductLabeled")!,
            nbReadyToShip: counterMap.get("NbProductReadyToShip")!,
            nbShipped: counterMap.get("NbProductShipped")!,
            nbCanceled: undefined,
            nbStockout: counterMap.get("NbProductStockout")!,
            nbTotal: counterMap.get("NbProduct")!,
        });
    };

    const renderCounterColumns = (
        counter: { nbAvailable?: number,
            nbParcelled?: number,
            nbLabeled?: number,
            nbReadyToShip?: number,
            nbShipped?: number,
            nbCanceled?: number,
            nbStockout?: number,
            nbTotal?: number }) => {
        return [
            getCounter("nbAvailable", counter.nbAvailable, counter.nbTotal),
            getCounter("nbParcelled", counter.nbParcelled, counter.nbTotal),
            getCounter("nbLabeled", counter.nbLabeled, counter.nbTotal),
            context.hasPermission(Permission.FeatureReadyToShip) ? getCounter("nbReadyToShip", counter.nbReadyToShip, counter.nbTotal) : undefined,
            getCounter("nbShipped", counter.nbShipped, counter.nbTotal),
            getCounter("nbCanceled", counter.nbCanceled, undefined),
            IsFeatureToggleEnabled(FeatureToggle.STOCKOUTS) ? getCounter("nbStockout", counter.nbStockout, counter.nbTotal) : undefined,
            <TableCell key="nbTotal">{counter.nbTotal}</TableCell>,
        ];
    };

    const getCounter = (key: string, value?: number, total?: number) => {
        if (value === undefined) {
            return;
        }

        if (total === undefined) {
            return [
                <TableCell className="counter-value" key={key}>{value}</TableCell>,
                <TableCell className="counter-percent" key={`${key}-percent`}></TableCell>,
            ];
        }

        const percent = total === 0 ? 0 : Math.round((value * 10000) / total) / 100;
        return [
            <TableCell className="counter-value" key={key}>{value}</TableCell>,
            <TableCell className="counter-percent" key={`${key}-percent`}>{`(${percent} %)`}</TableCell>,
        ];
    };

    return <div>
        {isOperationDataValidationPopupOpen && params.operationCode &&
            <OperationDataValidationPopup operation={params.operationCode}
                                          onClose={() => setIsOperationDataValidationPopupOpen(false)} />}
        {isOperationDataReportPopupOpen && params.operationCode &&
            <OperationDataReportPopup operation={params.operationCode}
                                      onClose={() => setIsOperationDataReportPopupOpen(false)} />}
        {renderFilter()}
        <Table>
            <TableHead className="reporting-header">
                {renderProgressModeHeaders(filter.mode)}
            </TableHead>
            <TableBody className="reporting-body">
                {batchProgressList.length > 1 ?
                    renderProgress(generalCounterMap, false)
                    : undefined}
                {orderBy(batchProgressList, batchProgress => batchProgress.batchCreationDate)
                    .map(batchProgress => renderBatchProgress(batchProgress))}
            </TableBody>
        </Table>
    </div>;
};
